import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Settlement {
  id: number;
  customerName: string;
  manager: string;
  contractDate: string;
  shootingDate: string;
  weddingDate: string;
  totalAmount: number;
  settlementAmount: number;
  downPayment: number;
  middlePayment: number;
  finalPayment: number;
  status: string;
  category: string;
  // 새로운 필드들
  vendor: string;
  orderNumber: string;
  invoiceNumber?: string;
  taxInvoiceNumber?: string;
  paymentMethod: string;
  paymentDate?: string;
  taxInvoiceDate?: string;
  settlementDate?: string;
  commissionRate: number;
  commissionAmount: number;
  netAmount: number;
}

export default function SettlementManagement() {
  const [settlements, setSettlements] = useState<Settlement[]>([
    {
      id: 1,
      customerName: '김미영',
      manager: '본식매니저',
      contractDate: '2024-01-15',
      shootingDate: '2024-03-10',
      weddingDate: '2024-03-15',
      totalAmount: 15000000,
      settlementAmount: 13500000,
      downPayment: 5000000,
      middlePayment: 5000000,
      finalPayment: 5000000,
      status: '정산완료',
      category: '본식',
      vendor: '강남드레스샵',
      orderNumber: 'ORD-2024-001',
      invoiceNumber: 'INV-2024-001',
      taxInvoiceNumber: 'TAX-2024-001',
      paymentMethod: '계좌이체',
      paymentDate: '2024-03-04',
      taxInvoiceDate: '2024-03-05',
      settlementDate: '2024-03-06',
      commissionRate: 10,
      commissionAmount: 1500000,
      netAmount: 13500000
    },
    {
      id: 2,
      customerName: '박지훈',
      manager: '혼수매니저',
      contractDate: '2024-02-01',
      shootingDate: '2024-04-15',
      weddingDate: '2024-04-20',
      totalAmount: 8000000,
      settlementAmount: 7200000,
      downPayment: 3000000,
      middlePayment: 2500000,
      finalPayment: 2500000,
      status: '결제완료',
      category: '혼수',
      vendor: '신촌정장샵',
      orderNumber: 'ORD-2024-002',
      invoiceNumber: 'INV-2024-002',
      paymentMethod: '법인카드',
      paymentDate: '2024-04-10',
      commissionRate: 10,
      commissionAmount: 800000,
      netAmount: 7200000
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('전체');
  const [selectedManager, setSelectedManager] = useState('전체');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [selectedTab, setSelectedTab] = useState('전체');

  const statuses = ['전체', '결제대기', '결제완료', '정산대기', '정산완료'];
  const managers = ['전체', '본식매니저', '혼수매니저', '리허설매니저'];
  const categories = ['전체', '본식', '혼수', '리허설'];
  const tabs = ['전체', '업체별정산', '세금계산서', '수수료정산'];

  const filteredSettlements = settlements.filter(settlement => {
    const statusMatch = selectedStatus === '전체' || settlement.status === selectedStatus;
    const managerMatch = selectedManager === '전체' || settlement.manager === selectedManager;
    const categoryMatch = selectedCategory === '전체' || settlement.category === selectedCategory;
    return statusMatch && managerMatch && categoryMatch;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '결제대기':
      case '정산대기':
        return 'bg-yellow-100 text-yellow-800';
      case '결제완료':
        return 'bg-blue-100 text-blue-800';
      case '정산완료':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 통계 계산
  const totalContractAmount = settlements.reduce((sum, item) => sum + item.totalAmount, 0);
  const totalSettlementAmount = settlements.reduce((sum, item) => sum + item.settlementAmount, 0);
  const totalCommissionAmount = settlements.reduce((sum, item) => sum + item.commissionAmount, 0);
  const totalNetAmount = settlements.reduce((sum, item) => sum + item.netAmount, 0);
  const completedSettlements = settlements.filter(s => s.status === '정산완료').length;
  const pendingSettlements = settlements.filter(s => s.status === '정산대기').length;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">정산관리</h1>
              <p className="text-gray-600 mt-1">업체별 정산 현황과 세금계산서를 관리하세요</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              정산 등록
            </button>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">총 계약금액</p>
                  <p className="text-xl font-bold text-blue-900">{formatCurrency(totalContractAmount)}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-blue-600 text-lg"></i>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700">정산금액</p>
                  <p className="text-xl font-bold text-green-900">{formatCurrency(totalSettlementAmount)}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-exchange-dollar-line text-green-600 text-lg"></i>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-700">수수료</p>
                  <p className="text-xl font-bold text-purple-900">{formatCurrency(totalCommissionAmount)}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-percent-line text-purple-600 text-lg"></i>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-indigo-700">순정산액</p>
                  <p className="text-xl font-bold text-indigo-900">{formatCurrency(totalNetAmount)}</p>
                </div>
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <i className="ri-calculator-line text-indigo-600 text-lg"></i>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-700">정산대기</p>
                  <p className="text-2xl font-bold text-yellow-900">{pendingSettlements}건</p>
                </div>
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-yellow-600 text-lg"></i>
                </div>
              </div>
            </div>

            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">정산완료</p>
                  <p className="text-2xl font-bold text-emerald-900">{completedSettlements}건</p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <i className="ri-check-line text-emerald-600 text-lg"></i>
                </div>
              </div>
            </div>
          </div>

          {/* 정산 탭 */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="flex gap-2 mb-4">
              {tabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedTab === tab
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* 필터 */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="flex gap-4 items-center">
              <span className="text-sm font-medium text-gray-700">상태:</span>
              <div className="flex gap-2">
                {statuses.map(status => (
                  <button
                    key={status}
                    onClick={() => setSelectedStatus(status)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedStatus === status
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
              
              <span className="text-sm font-medium text-gray-700 ml-4">담당자:</span>
              <div className="flex gap-2">
                {managers.map(manager => (
                  <button
                    key={manager}
                    onClick={() => setSelectedManager(manager)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedManager === manager
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {manager}
                  </button>
                ))}
              </div>

              <span className="text-sm font-medium text-gray-700 ml-4">구분:</span>
              <div className="flex gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 정산 카드 목록 */}
          <div className="space-y-4">
            {filteredSettlements.map((settlement) => (
              <div key={settlement.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-building-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">{settlement.vendor}</h3>
                      <p className="text-sm text-gray-600">{settlement.customerName} • {settlement.manager}</p>
                      <p className="text-xs text-gray-500">{settlement.orderNumber}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(settlement.status)}`}>
                      {settlement.status}
                    </span>
                  </div>
                </div>

                {/* 결제 정보 */}
                <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">결제방법</p>
                    <p className="text-sm font-medium">{settlement.paymentMethod}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">결제일</p>
                    <p className="text-sm font-medium">{settlement.paymentDate || '-'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">세금계산서</p>
                    <p className="text-sm font-medium">{settlement.taxInvoiceNumber || '미발행'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">정산일</p>
                    <p className="text-sm font-medium">{settlement.settlementDate || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">계약일</p>
                    <p className="text-sm font-medium">{settlement.contractDate}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">촬영일</p>
                    <p className="text-sm font-medium">{settlement.shootingDate}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">본식일</p>
                    <p className="text-sm font-medium">{settlement.weddingDate}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">총 계약금</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(settlement.totalAmount)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-purple-600">수수료 ({settlement.commissionRate}%)</p>
                      <p className="text-lg font-bold text-purple-600">-{formatCurrency(settlement.commissionAmount)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-blue-600">정산금액</p>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(settlement.settlementAmount)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-green-600">순정산액</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(settlement.netAmount)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mt-3 p-3 bg-blue-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-blue-600">계약금</p>
                      <p className="text-sm font-medium">{formatCurrency(settlement.downPayment)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-blue-600">중도금</p>
                      <p className="text-sm font-medium">{formatCurrency(settlement.middlePayment)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-blue-600">잔금</p>
                      <p className="text-sm font-medium">{formatCurrency(settlement.finalPayment)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    {settlement.invoiceNumber && (
                      <span><i className="ri-bill-line mr-1"></i>인보이스: {settlement.invoiceNumber}</span>
                    )}
                    {settlement.taxInvoiceNumber && (
                      <span><i className="ri-receipt-line mr-1"></i>세금계산서: {settlement.taxInvoiceNumber}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button className="text-blue-600 hover:text-blue-800 w-8 h-8 flex items-center justify-center">
                      <i className="ri-edit-line"></i>
                    </button>
                    <button className="text-green-600 hover:text-green-800 w-8 h-8 flex items-center justify-center">
                      <i className="ri-file-download-line"></i>
                    </button>
                    <button className="text-purple-600 hover:text-purple-800 w-8 h-8 flex items-center justify-center">
                      <i className="ri-receipt-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 정산 등록 모달 */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">정산 등록</h2>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
                
                <form className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                        <option>본식매니저</option>
                        <option>혼수매니저</option>
                        <option>리허설매니저</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약일</label>
                      <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">촬영일</label>
                      <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식일</label>
                      <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">총 계약금액</label>
                      <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">정산금액</label>
                      <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약금</label>
                      <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">중도금</label>
                      <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">잔금</label>
                      <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">구분</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                      <option>본식</option>
                      <option>혼수</option>
                      <option>리허설</option>
                    </select>
                  </div>
                  
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
                    >
                      등록
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
